import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Snowflake, Thermometer, Layers, Activity } from "lucide-react";

const GRAIN_COLORS = ["#ff6b35", "#ffb703", "#00e5ff", "#00ff88"];

const tooltipStyle = {
    contentStyle: { background: "#12141d", border: "1px solid #2a2e3d", borderRadius: 8, fontFamily: "JetBrains Mono, monospace", fontSize: 12 },
    labelStyle: { color: "#f1f5f9" },
    itemStyle: { color: "#f1f5f9" },
};

function ChartCard({ title, icon: Icon, accent, children, testid }) {
    return (
        <div className="rounded-xl border border-ares-border bg-ares-dark/90 p-5" data-testid={testid}>
            <div className="flex items-center gap-2 mb-4">
                <Icon className={`w-4 h-4 ${accent}`} />
                <h3 className="font-mono text-[11px] uppercase tracking-widest text-ares-muted">{title}</h3>
            </div>
            <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
            </div>
        </div>
    );
}

export default function TelemetryHub() {
    const [regions, setRegions] = useState([]);
    const [regionId, setRegionId] = useState("");
    const region = regions.find((r) => r.id === regionId);

    useEffect(() => {
        api.get("/regions").then(({ data }) => {
            setRegions(data);
            setRegionId(data[3]?.id || data[0]?.id);
        });
    }, []);

    if (!region) return null;

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div>
                    <h2 className="font-heading text-xl font-semibold">Subsurface & Surface Telemetry</h2>
                    <p className="text-sm text-ares-muted">Radar, thermal and regolith profiles for the selected region.</p>
                </div>
                <Select value={regionId} onValueChange={setRegionId}>
                    <SelectTrigger className="w-60 bg-ares-dark border-ares-border" data-testid="telemetry-region-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-ares-panel border-ares-border">
                        {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <ChartCard title="Subsurface Water-Equivalent Hydrogen (0–5m)" icon={Snowflake} accent="text-ares-cyan" testid="chart-ice-profile">
                    <AreaChart data={region.ice_depth_profile} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                        <defs>
                            <linearGradient id="iceGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#00e5ff" stopOpacity={0.6} />
                                <stop offset="100%" stopColor="#00e5ff" stopOpacity={0.05} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3d" />
                        <XAxis dataKey="depth_m" unit="m" stroke="#8c94a8" fontSize={11} />
                        <YAxis unit="%" stroke="#8c94a8" fontSize={11} />
                        <Tooltip {...tooltipStyle} />
                        <Area type="monotone" dataKey="weh_pct" stroke="#00e5ff" strokeWidth={2} fill="url(#iceGrad)" name="WEH" />
                    </AreaChart>
                </ChartCard>

                <ChartCard title="Surface Temperature over one SOL" icon={Thermometer} accent="text-ares-orange" testid="chart-temperature">
                    <LineChart data={region.temperature_sol} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3d" />
                        <XAxis dataKey="hour" unit="h" stroke="#8c94a8" fontSize={11} />
                        <YAxis unit="°" stroke="#8c94a8" fontSize={11} />
                        <Tooltip {...tooltipStyle} />
                        <Line type="monotone" dataKey="temp_c" stroke="#ff6b35" strokeWidth={2} dot={{ fill: "#ff6b35", r: 3 }} name="Temp °C" />
                    </LineChart>
                </ChartCard>

                <ChartCard title="Regolith Grain-Size Distribution" icon={Layers} accent="text-ares-amber" testid="chart-grain-size">
                    <PieChart>
                        <Pie data={region.grain_size} dataKey="pct" nameKey="type" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                            {region.grain_size.map((_, i) => <Cell key={i} fill={GRAIN_COLORS[i % GRAIN_COLORS.length]} stroke="#12141d" strokeWidth={2} />)}
                        </Pie>
                        <Tooltip {...tooltipStyle} />
                    </PieChart>
                </ChartCard>

                <ChartCard title="Ice Probability vs Slope by Region" icon={Activity} accent="text-ares-emerald" testid="chart-region-compare">
                    <BarChart data={regions.map((r) => ({ name: r.name.split(" ")[0], ice: Math.round(r.ice_probability * 100), slope: r.slope_deg }))} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3d" />
                        <XAxis dataKey="name" stroke="#8c94a8" fontSize={10} />
                        <YAxis stroke="#8c94a8" fontSize={11} />
                        <Tooltip {...tooltipStyle} />
                        <Bar dataKey="ice" fill="#00e5ff" name="Ice %" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="slope" fill="#ffb703" name="Slope °" radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ChartCard>
            </div>

            {/* Grain legend */}
            <div className="mt-4 flex flex-wrap gap-4 justify-center">
                {region.grain_size.map((g, i) => (
                    <div key={g.type} className="flex items-center gap-2 font-mono text-xs text-ares-muted">
                        <span className="w-3 h-3 rounded-sm" style={{ background: GRAIN_COLORS[i % GRAIN_COLORS.length] }} />
                        {g.type} · {g.pct}%
                    </div>
                ))}
            </div>
        </div>
    );
}
